import React from "react";
import {
  Autocomplete,
  Avatar,
  Chip,
  FormControl,
  FormHelperText,
  ListItem,
  ListItemText,
  TextField,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import fbFavIcon from "../../../../../assets/images/fb-fav-icon.svg";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function AutoCompleteFilter(props: any) {
  const {
    options,
    onChange,
    disableClearable = false,
    value,
    required,
    label,
    id,
    validState = true,
    listType,
    width,
    multiSelect = false,
  } = props as {
    options: any[];
    onChange: (event: any, value: any) => void;
    disableClearable?: boolean;
    value?: any;
    required?: boolean;
    label?: string;
    id?: string;
    validState?: boolean;
    listType:
    | "BRANCH"
    | "COURSE"
    | "BATCH"
    | "COUNTRY"
    | "MONTH"
    | "YEAR"
    | "ASSIGNEE"
    | "FACEBOOK"
    | "TYPE";
    width?: string;
    multiSelect?: boolean;
  };
  const getOptionLabel = (option: any) => {
    switch (listType) {
      case "BRANCH":
        return option?.branchName || "";
      case "COURSE":
        return option?.courseName || "";
      case "BATCH":
        return option?.batchName || "";
      case "COUNTRY":
        return option?.name || "";
      case "MONTH":
        return option?.value || "";
      case "YEAR":
        return option?.toString() || "";
      case "ASSIGNEE":
        return `${option?.firstName || ""} ${option?.lastName || ""}`.trim();
      case "FACEBOOK":
        return option?.label || "";
      case "TYPE":
        return option?.value || "";
      default:
        return "";
    }
  };

  return (
    <FormControl
      fullWidth
      variant="standard"
      className="formControl"
      sx={{ width: width || "250px" }}
    >
      {label && (
        <div
          style={{
            fontSize: "12px",
            color: "#142C44",
            fontWeight: "500",
            fontFamily: "Poppins",
            display: "flex",
            marginBottom: "5px",
          }}
        >
          {label}
          {required && (
            <span style={{ marginLeft: "4px", color: "#D32F2F" }}>*</span>
          )}
        </div>
      )}

      {options && (
        <Autocomplete
          disablePortal
          multiple={multiSelect}
          id={id}
          options={options}
          getOptionLabel={getOptionLabel}
          fullWidth
          onChange={onChange}
          value={multiSelect ? value ?? [] : value ?? null}
          disableClearable={disableClearable}
          isOptionEqualToValue={(option: any, value: any) => {
            if (!option || !value) return false;

            switch (listType) {
              case "BRANCH":
              case "COURSE":
              case "BATCH":
                return option.id === value.id;
              case "COUNTRY":
                return option.name === value.name;
              case "TYPE":
                return option.id === value.id;
              case "MONTH":
                return option.value === value.value;
              case "YEAR":
                return option === value;
              case "ASSIGNEE":
                return option.id === value.id;
              case "FACEBOOK":
                return option.label === value.label;
              default:
                return option === value;
            }
          }}
          renderInput={(params) => (
            <TextField
              variant="outlined"
              {...params}
              size="small"
              error={!validState}
            />
          )}
          renderTags={(selectedValues, getTagProps) => {
            const valuesArray = multiSelect
              ? selectedValues
              : selectedValues
                ? [selectedValues]
                : [];

            return valuesArray?.map((option, index) => (
              <Chip
                label={
                  listType === "FACEBOOK" ? (
                    <div
                      style={{
                        display: "flex",
                      }}
                    >
                      {option?.label}
                    </div>
                  ) : (
                    getOptionLabel(option)
                  )
                }
                {...getTagProps({ index })}
                deleteIcon={<CloseIcon sx={{ fontSize: "14px" }} />}
                avatar={
                  listType === "ASSIGNEE" ? (
                    <Avatar
                      sx={{
                        background: "#F5F5F7",
                        border: "1px solid #EEEEF4",
                        textTransform: "capitalize",
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#667085",
                      }}
                    >
                      {option?.firstName?.charAt(0)}
                    </Avatar>
                  ) : listType === "FACEBOOK" ? (
                    <div
                      style={{
                        width: "15px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "2px",
                      }}
                    >
                      <img src={fbFavIcon} alt="icon" />
                    </div>
                  ) : undefined
                }
                sx={{
                  background: listType === "FACEBOOK" ? "#fff" : "#EFEFEF",
                  textTransform: "capitalize",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: listType === "FACEBOOK" ? "#073E53" : "#667085",
                  borderRadius: "4px",
                  border:
                    listType === "FACEBOOK" ? "1px solid #809BA6B2" : "none",
                }}
              />
            ));
          }}
          renderOption={(props, option, { selected }) => (
            <ListItem {...props} key={option?.id || option?.name}>
              {multiSelect && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              <ListItemText
                primary={
                  listType === "ASSIGNEE" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        textTransform: "capitalize",
                        color: "#667085",
                        fontSize: "14px",
                      }}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: "#F5F5F7",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 6,
                          textTransform: "uppercase",
                          color: "#667085",
                          fontSize: "10px",
                          fontWeight: "500",
                          padding: "5px 7px 3px 7px",
                        }}
                      >
                        {option?.firstName?.[0]} {option?.lastName?.[0]}
                      </div>
                      {`${option?.firstName} ${option?.lastName}`}
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: "14px",
                        color:
                          option.isBatchCompleted === true ? "red" : "#142C44",
                        fontFamily: "poppins",
                        textTransform: "capitalize",
                      }}
                    >
                      {getOptionLabel(option)}
                    </div>
                  )
                }
              />
            </ListItem>
          )}
          ListboxProps={{
            style: {
              maxHeight: "230px",
              fontSize: "14px",
              color: "#142C44",
              fontFamily: "Poppins",
              textTransform: "capitalize",
            },
          }}
        />
      )}
      {!validState && required && (
        <FormHelperText error>Field required</FormHelperText>
      )}
    </FormControl>
  );
}

export default AutoCompleteFilter;
